import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type * as React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';
import { ROUTE_ID_UUID_RE } from './_authenticated.routes.$routeId';

function isRouteIdParam(value: unknown): value is string {
  return typeof value === 'string' && ROUTE_ID_UUID_RE.test(value);
}

// Trailing `_` on the `$routeId_` filename segment escapes this route
// from being treated as a CHILD of `_authenticated.routes.$routeId`.
// Without the escape, TanStack Router nests `/routes/$routeId/build`
// under the route detail page — and since the detail page is a leaf
// (no <Outlet />), matching the child renders only the parent. The
// underscore is stripped from the user-facing URL (`fullPath` stays
// `/routes/$routeId/build`); the parent in the generated tree
// becomes `AuthenticatedRoute`, the auth shell that DOES render
// <Outlet />.
//
// Because this route is no longer a child of the detail route, the
// detail route's UUID-shape parseParams no longer runs ahead of it.
// Mirror the validator inline so a malformed UUID surfaces 404
// immediately rather than after a supabase round-trip.
export const Route = createFileRoute('/_authenticated/routes/$routeId_/build')({
  component: RouteBuilderPage,
  parseParams: (params): { routeId: string } => {
    if (!isRouteIdParam(params.routeId)) {
      throw notFound();
    }
    return { routeId: params.routeId };
  },
});

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

type RouteRow = {
  id: string;
  tenant_id: string;
  pharmacy_id: string;
  route_status: string;
  planned_for: string;
};

// Pool entry: a pending order whose dropoff stop is not yet on any
// route. The dropoff_stop_id is what the reorder/add RPCs need to
// finalize sequencing; we cache it on the pool row so Save doesn't
// need an extra round-trip.
type PoolOrder = {
  id: string;
  order_number: string;
  patient_name: string | null;
  address: string;
  city: string | null;
  dropoff_stop_id: string;
};

// Stops currently on the route. Pickup is locked at sequence 0 and
// rendered separately from the sortable list; dropoffs join their
// owning order(s) for the card label.
type RouteStop = {
  id: string;
  stop_type: 'pickup' | 'dropoff';
  sequence_number: number | null;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  orders: {
    id: string;
    order_number: string;
    patient_name: string | null;
  }[];
};

type DataState =
  | { kind: 'loading' }
  | {
      kind: 'loaded';
      route: RouteRow;
      pickup: RouteStop | null;
      // Server-side dropoff stops at the moment of last load. Used as
      // the diff baseline against the working set.
      serverDropoffs: RouteStop[];
      // Server-side pending pool at the moment of last load.
      serverPool: PoolOrder[];
    }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

// Working state — what the UI displays + edits. Derived from server
// state on (re)load; mutated by drag handlers and the remove button.
// Tracked as two ordered orderId arrays: pool (left column) and route
// (right column, dropoffs only). The pickup is server-owned and not
// part of the working set.
type WorkingState = {
  poolOrderIds: string[];
  routeOrderIds: string[];
};

// ---------------------------------------------------------------------------
// Error message mapping
// ---------------------------------------------------------------------------

function friendlyBuilderError(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LYNRX_ROUTE_NOT_FOUND'))
    return 'Route no longer accessible. Refresh.';
  if (m.includes('LYNRX_ROUTE_NOT_DRAFT'))
    return 'This route is no longer in draft. Refresh to see its current state.';
  if (m.includes('LYNRX_INCOMPLETE_REORDER'))
    return 'Server rejected the reorder. Refresh and try again.';
  if (m.includes('LYNRX_INVALID_STOP_SET'))
    return 'One or more stops are no longer valid on this route. Refresh.';
  if (m.includes('LYNRX_ORDER_NOT_ADDABLE'))
    return 'One of the selected orders is no longer addable. Refresh.';
  if (m.includes('LYNRX_ORDER_NOT_PENDING'))
    return "An order in the selection isn't pending anymore. Refresh.";
  if (m.includes('LYNRX_ORDER_ON_OTHER_ROUTE'))
    return 'An order is already on a different route. Refresh.';
  if (m.includes('LYNRX_ORDER_PHARMACY_MISMATCH'))
    return "An order isn't in this route's pharmacy. Refresh.";
  return 'Save failed. Try again.';
}

// ---------------------------------------------------------------------------
// Header / list helpers
// ---------------------------------------------------------------------------

function formatPlannedFor(iso: string): string {
  const parts = iso.split('-');
  if (parts.length === 3) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const local = new Date(y, m - 1, d);
      return local.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return iso;
}

function poolAddress(o: PoolOrder): string {
  return [o.address, o.city].filter(Boolean).join(', ');
}

function dropoffAddress(s: RouteStop): string {
  return [s.address, s.city].filter(Boolean).join(', ');
}

function dropoffPatientLabel(s: RouteStop): string {
  const first = s.orders[0]?.patient_name ?? '—';
  if (s.orders.length <= 1) return first;
  return `${first} + ${s.orders.length - 1} more`;
}

function dropoffOrderNumber(s: RouteStop): string {
  return s.orders[0]?.order_number ?? '—';
}

// ---------------------------------------------------------------------------
// Router shell + view split (test seam mirrors the route detail page)
// ---------------------------------------------------------------------------

function RouteBuilderPage(): JSX.Element {
  const { routeId } = Route.useParams();
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/routes/$routeId', params: { routeId } });
  }, [navigate, routeId]);
  return <RouteBuilderView routeId={routeId} onBack={onBack} />;
}

type ViewProps = {
  routeId: string;
  onBack: () => void;
  client?: LynrxSupabaseClient;
};

export function RouteBuilderView({
  routeId,
  onBack,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [state, setState] = useState<DataState>({ kind: 'loading' });

  const headerTitle =
    state.kind === 'loaded'
      ? `Build · ${formatPlannedFor(state.route.planned_for)}`
      : 'Build route';
  useSetPageHeader({
    title: headerTitle,
    parentLabel: 'Routes',
    parentTo: '/routes',
  });

  const loadAll = useCallback(async () => {
    setState({ kind: 'loading' });
    // Route first. We need its pharmacy_id to scope the pool query.
    const routeResult = await client
      .from('routes')
      .select('id, tenant_id, pharmacy_id, route_status, planned_for')
      .eq('id', routeId)
      .is('deleted_at', null)
      .maybeSingle();

    if (routeResult.error) {
      captureSupabaseError(routeResult.error, 'routes.build.fetch_route');
      setState({ kind: 'error', message: 'Could not load this route.' });
      return;
    }
    if (!routeResult.data) {
      setState({ kind: 'not_found' });
      return;
    }
    const route = routeResult.data as unknown as RouteRow;

    const stopsQuery = client
      .from('stops')
      .select(
        'id, stop_type, sequence_number, address, city, state, zip, orders!orders_dropoff_stop_id_fkey(id, order_number, patient_name)',
      )
      .eq('route_id', routeId)
      .is('deleted_at', null)
      .order('sequence_number', { ascending: true });

    // Pool query: pending orders in this route's pharmacy whose
    // dropoff stop is not on any route (a pending order should always
    // satisfy that, but join + filter defensively).
    const poolQuery = client
      .from('orders')
      .select(
        'id, order_number, patient_name, dropoff_stop:stops!orders_dropoff_stop_id_fkey(id, address, city, route_id)',
      )
      .eq('pharmacy_id', route.pharmacy_id)
      .eq('status', 'pending')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    const [stopsResult, poolResult] = await Promise.all([stopsQuery, poolQuery]);

    if (stopsResult.error) {
      captureSupabaseError(stopsResult.error, 'routes.build.fetch_stops');
      setState({ kind: 'error', message: 'Could not load route stops.' });
      return;
    }
    if (poolResult.error) {
      captureSupabaseError(poolResult.error, 'routes.build.fetch_pool');
      setState({ kind: 'error', message: 'Could not load unassigned orders.' });
      return;
    }

    const allStops = (stopsResult.data ?? []) as unknown as RouteStop[];
    const pickup = allStops.find((s) => s.stop_type === 'pickup') ?? null;
    const serverDropoffs = allStops
      .filter((s) => s.stop_type === 'dropoff')
      .slice()
      .sort((a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0));

    type PoolRow = {
      id: string;
      order_number: string;
      patient_name: string | null;
      dropoff_stop:
        | { id: string; address: string; city: string | null; route_id: string | null }
        | null;
    };
    const poolRows = (poolResult.data ?? []) as unknown as PoolRow[];
    const serverPool: PoolOrder[] = poolRows
      .filter((r) => r.dropoff_stop && r.dropoff_stop.route_id === null)
      .map((r) => ({
        id: r.id,
        order_number: r.order_number,
        patient_name: r.patient_name,
        address: r.dropoff_stop?.address ?? '',
        city: r.dropoff_stop?.city ?? null,
        dropoff_stop_id: r.dropoff_stop?.id ?? '',
      }));

    setState({
      kind: 'loaded',
      route,
      pickup,
      serverDropoffs,
      serverPool,
    });
  }, [client, routeId]);

  useEffect(() => {
    if (!isReady || !session) return;
    void loadAll();
  }, [isReady, session, loadAll]);

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      <div className="mb-4">
        <Link
          to="/routes/$routeId"
          params={{ routeId }}
          className="text-sm text-primary hover:underline"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onBack();
          }}
        >
          ← Back to route
        </Link>
      </div>

      {state.kind === 'loading' ? (
        <Card>
          <p className="text-sm text-ink-2">Loading builder…</p>
        </Card>
      ) : null}

      {state.kind === 'not_found' ? (
        <Alert tone="error" title="Route not found">
          We couldn&apos;t find a route with that ID.
          <div className="mt-3">
            <Button variant="secondary" onClick={onBack}>
              Back to route
            </Button>
          </div>
        </Alert>
      ) : null}

      {state.kind === 'error' ? (
        <Alert tone="error" title="Something went wrong">
          {state.message}
        </Alert>
      ) : null}

      {state.kind === 'loaded' ? (
        <BuilderBoard
          routeId={routeId}
          route={state.route}
          pickup={state.pickup}
          serverDropoffs={state.serverDropoffs}
          serverPool={state.serverPool}
          client={client}
          onReload={loadAll}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Builder board — owns the working state + dnd interactions
// ---------------------------------------------------------------------------

function BuilderBoard({
  routeId,
  route,
  pickup,
  serverDropoffs,
  serverPool,
  client,
  onReload,
}: {
  routeId: string;
  route: RouteRow;
  pickup: RouteStop | null;
  serverDropoffs: RouteStop[];
  serverPool: PoolOrder[];
  client: LynrxSupabaseClient;
  onReload: () => Promise<void>;
}): JSX.Element {
  const isDraft = route.route_status === 'draft';

  // Build an order-by-id lookup that's stable across drags. Includes
  // every order we know about (pool + route) so we can look up
  // metadata regardless of which container an id currently lives in.
  const ordersIndex = useMemo(() => {
    const map = new Map<string, PoolOrder>();
    for (const o of serverPool) map.set(o.id, o);
    for (const stop of serverDropoffs) {
      for (const order of stop.orders) {
        if (!map.has(order.id)) {
          map.set(order.id, {
            id: order.id,
            order_number: order.order_number,
            patient_name: order.patient_name,
            address: stop.address,
            city: stop.city,
            dropoff_stop_id: stop.id,
          });
        }
      }
    }
    return map;
  }, [serverPool, serverDropoffs]);

  // Map an orderId → stopId. Needed by Save to translate the working
  // route ordering into the dropoff_stop_ids array expected by
  // reorder_route_stops.
  const orderToStopId = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of serverPool) map.set(o.id, o.dropoff_stop_id);
    for (const stop of serverDropoffs) {
      for (const order of stop.orders) {
        map.set(order.id, stop.id);
      }
    }
    return map;
  }, [serverPool, serverDropoffs]);

  // Server-side ordered orderId arrays — the diff baseline.
  const serverRouteOrderIds = useMemo(
    () =>
      serverDropoffs.flatMap((s) => s.orders.map((o) => o.id)),
    [serverDropoffs],
  );
  const serverPoolOrderIds = useMemo(
    () => serverPool.map((o) => o.id),
    [serverPool],
  );

  const [working, setWorking] = useState<WorkingState>({
    poolOrderIds: serverPoolOrderIds,
    routeOrderIds: serverRouteOrderIds,
  });

  // Re-baseline when the server snapshot changes (after a Save reload
  // or an out-of-band update). This is a controlled reset — any
  // in-flight working state is dropped on purpose.
  useEffect(() => {
    setWorking({
      poolOrderIds: serverPoolOrderIds,
      routeOrderIds: serverRouteOrderIds,
    });
  }, [serverPoolOrderIds, serverRouteOrderIds]);

  // Search filter for the pool (client-side, applied to the current
  // working pool — items dragged out of the pool stop matching the
  // filter naturally).
  const [poolQuery, setPoolQuery] = useState('');
  const visiblePoolOrderIds = useMemo(() => {
    const q = poolQuery.trim().toLowerCase();
    if (q === '') return working.poolOrderIds;
    return working.poolOrderIds.filter((id) => {
      const o = ordersIndex.get(id);
      if (!o) return false;
      return (
        o.order_number.toLowerCase().includes(q) ||
        (o.patient_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [poolQuery, working.poolOrderIds, ordersIndex]);

  // Dirty diff. Compares working route order to server route order.
  const dirtyState = useMemo(() => {
    const serverSet = new Set(serverRouteOrderIds);
    const workingSet = new Set(working.routeOrderIds);
    const added = working.routeOrderIds.filter((id) => !serverSet.has(id));
    const removed = serverRouteOrderIds.filter((id) => !workingSet.has(id));
    let reordered = false;
    if (added.length === 0 && removed.length === 0) {
      for (let i = 0; i < serverRouteOrderIds.length; i++) {
        if (serverRouteOrderIds[i] !== working.routeOrderIds[i]) {
          reordered = true;
          break;
        }
      }
    } else {
      reordered = true;
    }
    return { added, removed, reordered };
  }, [serverRouteOrderIds, working.routeOrderIds]);

  const dirtyCount =
    dirtyState.added.length + dirtyState.removed.length + (dirtyState.reordered ? 1 : 0);
  const isDirty =
    dirtyState.added.length > 0 ||
    dirtyState.removed.length > 0 ||
    dirtyState.reordered;

  // Sensors. PointerSensor with a small activation distance keeps
  // tap-to-click on the card surface working; KeyboardSensor enables
  // arrow-key reorder for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

  // Locating an item by id. Returns 'pool' or 'route' or null. The
  // sortable's getContainer helper would also work, but we own state
  // anyway — direct lookup is clearer.
  const containerOf = useCallback(
    (orderId: string): 'pool' | 'route' | null => {
      if (working.routeOrderIds.includes(orderId)) return 'route';
      if (working.poolOrderIds.includes(orderId)) return 'pool';
      return null;
    },
    [working],
  );

  const onDragStart = useCallback((e: DragStartEvent) => {
    setActiveOrderId(String(e.active.id));
  }, []);

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const activeId = String(e.active.id);
      const overId = e.over ? String(e.over.id) : null;
      setActiveOrderId(null);
      if (!overId) return;

      const from = containerOf(activeId);
      // overId is either another item id or a container sentinel.
      const overIsRouteContainer = overId === 'route-droppable';
      const overIsPoolContainer = overId === 'pool-droppable';
      const overContainer = overIsRouteContainer
        ? 'route'
        : overIsPoolContainer
          ? 'pool'
          : containerOf(overId);

      if (from === null || overContainer === null) return;

      // Pool → Pool. Per the brief, pool ordering doesn't matter to
      // the backend; the visible filter applies to the working set
      // anyway. No-op the reorder.
      if (from === 'pool' && overContainer === 'pool') {
        return;
      }

      // Route → Route. Reorder within the dropoff list.
      if (from === 'route' && overContainer === 'route') {
        if (activeId === overId || overIsRouteContainer) return;
        setWorking((prev) => {
          const oldIndex = prev.routeOrderIds.indexOf(activeId);
          const newIndex = prev.routeOrderIds.indexOf(overId);
          if (oldIndex === -1 || newIndex === -1) return prev;
          return {
            ...prev,
            routeOrderIds: arrayMove(prev.routeOrderIds, oldIndex, newIndex),
          };
        });
        return;
      }

      // Pool → Route. Append to the end of the route per the brief
      // (insert-at-position is deferred).
      if (from === 'pool' && overContainer === 'route') {
        setWorking((prev) => ({
          poolOrderIds: prev.poolOrderIds.filter((id) => id !== activeId),
          routeOrderIds: [...prev.routeOrderIds, activeId],
        }));
        return;
      }

      // Route → Pool. Move back to the top of the pool so the
      // dispatcher can see what they just unassigned.
      if (from === 'route' && overContainer === 'pool') {
        setWorking((prev) => ({
          poolOrderIds: [activeId, ...prev.poolOrderIds.filter((id) => id !== activeId)],
          routeOrderIds: prev.routeOrderIds.filter((id) => id !== activeId),
        }));
        return;
      }
    },
    [containerOf],
  );

  // Click-to-remove from a route card. Equivalent to dragging back to
  // the pool but keyboard-accessible without the dnd dance.
  const onRemoveFromRoute = useCallback((orderId: string) => {
    setWorking((prev) => {
      if (!prev.routeOrderIds.includes(orderId)) return prev;
      return {
        poolOrderIds: [orderId, ...prev.poolOrderIds.filter((id) => id !== orderId)],
        routeOrderIds: prev.routeOrderIds.filter((id) => id !== orderId),
      };
    });
  }, []);

  // Click-to-add from a pool card. Symmetric to remove; appends to
  // the route. Same as drag → end-of-route per the brief.
  const onAddToRoute = useCallback((orderId: string) => {
    setWorking((prev) => {
      if (!prev.poolOrderIds.includes(orderId)) return prev;
      return {
        poolOrderIds: prev.poolOrderIds.filter((id) => id !== orderId),
        routeOrderIds: [...prev.routeOrderIds, orderId],
      };
    });
  }, []);

  // ------------------------------- Save -------------------------------------

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  // Auto-clear success messages on the toast cadence used elsewhere
  // (route detail driver dropdown, orders bulk feedback).
  useEffect(() => {
    if (!saveSuccess) return;
    const handle = window.setTimeout(() => setSaveSuccess(null), 3500);
    return () => window.clearTimeout(handle);
  }, [saveSuccess]);

  const onDiscard = useCallback(() => {
    setSaveError(null);
    void onReload();
  }, [onReload]);

  // Route optimization used to live here on the dispatcher build
  // surface. It moved to the driver app (HomeScreen) — origin =
  // driver's current GPS, destination = driver home / GPS round-trip.
  // See apps/driver/src/screens/HomeScreen.tsx (onOptimize) and
  // apps/web/api/routes/driver-optimize.ts.

  // Helper: recover from any save-flow failure by grounding the
  // working state in fresh server truth and showing a banner that
  // tells the dispatcher exactly that. Avoids the "stranded UI with
  // bogus unsaved-changes count" dead-end (Bug 2). Returns once the
  // refetch completes so the caller can safely setSaving(false).
  const recoverFromSaveError = useCallback(
    async (friendlyMessage: string) => {
      await onReload();
      setSaveError(
        `${friendlyMessage} The view has been refreshed — try again.`,
      );
    },
    [onReload],
  );

  const onSave = useCallback(async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      // Order: add → remove → reorder. Sequencing matters: add must
      // run before reorder so the new stops are eligible for the
      // reorder validation; remove must run before reorder so the
      // recompaction logic doesn't fight the explicit ordering.
      if (dirtyState.added.length > 0) {
        const { error } = await client.rpc('add_orders_to_route', {
          p_route_id: routeId,
          p_order_ids: dirtyState.added,
        });
        if (error) {
          captureSupabaseError(error, 'routes.build.add_orders');
          await recoverFromSaveError(friendlyBuilderError(error.message));
          return;
        }
      }

      // remove_orders_from_route is skip-not-abort on the server: a
      // stale UI selection (already-pending, raced removal) is
      // silently ignored per row. The RPC returns the array of
      // order_ids that actually took effect — we use that, not the
      // optimistic input, to figure out what's still attached to the
      // route for the reorder completeness check (Bug 1).
      let serverRemoved: string[] = [];
      if (dirtyState.removed.length > 0) {
        const { data, error } = await client.rpc('remove_orders_from_route', {
          p_route_id: routeId,
          p_order_ids: dirtyState.removed,
        });
        if (error) {
          captureSupabaseError(error, 'routes.build.remove_orders');
          await recoverFromSaveError(friendlyBuilderError(error.message));
          return;
        }
        // PostgREST serializes uuid[] as a JSON array of strings. Be
        // defensive — coerce to a string[] regardless of shape.
        serverRemoved = Array.isArray(data)
          ? (data as unknown[]).map(String)
          : [];
      }

      // Always reorder if anything changed.
      //
      // The reorder input is "everything that's actually on the route
      // after add + remove ran server-side, in the order the user
      // intends." That's the user's working route ordering PLUS the
      // stops they wanted removed but the server didn't actually
      // detach (the optimistic-removed set minus the server-removed
      // set). The not-actually-removed stops get appended to the
      // working order — they're still on the route and the
      // completeness check would otherwise fail.
      if (
        dirtyState.added.length > 0 ||
        dirtyState.removed.length > 0 ||
        dirtyState.reordered
      ) {
        const removedActuallySet = new Set(serverRemoved);
        const stillOnRoute = dirtyState.removed.filter(
          (id) => !removedActuallySet.has(id),
        );
        const reorderOrderIds = [...working.routeOrderIds, ...stillOnRoute];
        const orderedStopIds = reorderOrderIds
          .map((id) => orderToStopId.get(id))
          .filter((s): s is string => Boolean(s));
        // Lookup miss → surface loudly via the recovery path; saving
        // a partial ordering would otherwise leave the route in an
        // undefined state per LYNRX_INCOMPLETE_REORDER.
        if (orderedStopIds.length !== reorderOrderIds.length) {
          await recoverFromSaveError(
            'Could not map every order to a stop.',
          );
          return;
        }
        if (orderedStopIds.length > 0) {
          const { error } = await client.rpc('reorder_route_stops', {
            p_route_id: routeId,
            p_dropoff_stop_ids: orderedStopIds,
          });
          if (error) {
            captureSupabaseError(error, 'routes.build.reorder');
            await recoverFromSaveError(friendlyBuilderError(error.message));
            return;
          }
        }
      }
      setSaveSuccess('Changes saved.');
      // Refetch to ground the working state in the server's new view.
      await onReload();
    } catch (err) {
      captureSupabaseError(err, 'routes.build.save.exception');
      await recoverFromSaveError(friendlyBuilderError(undefined));
    } finally {
      setSaving(false);
    }
  }, [
    client,
    dirtyState,
    isDirty,
    onReload,
    orderToStopId,
    recoverFromSaveError,
    routeId,
    saving,
    working.routeOrderIds,
  ]);

  // --------------------------------------------------------------------------

  if (!isDraft) {
    // Locked view — render the same lists in read-only form. No dnd,
    // no Save / Discard, no Remove buttons. The dispatcher unpublishes
    // from the route detail page if they need to edit.
    return (
      <>
        <div className="mb-4 rounded-md border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">This route is {route.route_status}.</span>{' '}
          Unpublish from the route detail page to edit stops.
        </div>
        <LockedView
          pickup={pickup}
          dropoffs={serverDropoffs}
          poolCount={serverPool.length}
        />
      </>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-bg px-4 py-3">
        <div className="text-sm text-ink">
          {isDirty ? (
            <>
              <span className="font-medium">
                {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
              </span>
              <span className="ml-2 text-ink-2">
                ({dirtyState.added.length} added · {dirtyState.removed.length}{' '}
                removed{dirtyState.reordered ? ' · reordered' : ''})
              </span>
            </>
          ) : (
            <span className="text-ink-2">No unsaved changes</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={onDiscard}
            disabled={!isDirty || saving}
          >
            Discard
          </Button>
          <Button onClick={onSave} disabled={!isDirty || saving} loading={saving}>
            Save
          </Button>
        </div>
      </div>

      {saveError ? (
        <div className="mb-4">
          <Alert tone="error">{saveError}</Alert>
        </div>
      ) : null}
      {saveSuccess ? (
        <div className="mb-4">
          <Alert tone="success">{saveSuccess}</Alert>
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <PoolColumn
            poolOrderIds={visiblePoolOrderIds}
            allPoolCount={working.poolOrderIds.length}
            ordersIndex={ordersIndex}
            query={poolQuery}
            onQueryChange={setPoolQuery}
            onAdd={onAddToRoute}
            activeOrderId={activeOrderId}
          />
          <RouteColumn
            pickup={pickup}
            routeOrderIds={working.routeOrderIds}
            ordersIndex={ordersIndex}
            onRemove={onRemoveFromRoute}
            activeOrderId={activeOrderId}
          />
        </div>
      </DndContext>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pool column
// ---------------------------------------------------------------------------

function PoolColumn({
  poolOrderIds,
  allPoolCount,
  ordersIndex,
  query,
  onQueryChange,
  onAdd,
  activeOrderId,
}: {
  poolOrderIds: string[];
  allPoolCount: number;
  ordersIndex: Map<string, PoolOrder>;
  query: string;
  onQueryChange: (q: string) => void;
  onAdd: (orderId: string) => void;
  activeOrderId: string | null;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: 'pool-droppable' });
  return (
    <section
      aria-label="Unassigned orders"
      className="flex min-h-[calc(100vh-220px)] flex-col rounded-md border border-border bg-bg-soft"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="text-sm font-medium text-ink">
          Unassigned orders{' '}
          <span className="text-ink-2 font-normal">({allPoolCount})</span>
        </h2>
        <input
          type="search"
          placeholder="Filter by order # or patient"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          aria-label="Filter unassigned orders"
          className="w-full max-w-xs rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary sm:w-auto"
        />
      </header>
      <div
        ref={setNodeRef}
        className={[
          'flex-1 overflow-y-auto p-3',
          isOver ? 'bg-bg-soft/60 outline outline-2 outline-primary/40' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {poolOrderIds.length === 0 ? (
          <EmptyPool query={query} allPoolCount={allPoolCount} />
        ) : (
          <SortableContext
            items={poolOrderIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2">
              {poolOrderIds.map((id) => {
                const o = ordersIndex.get(id);
                if (!o) return null;
                return (
                  <PoolCard
                    key={id}
                    order={o}
                    onAdd={onAdd}
                    isDragging={id === activeOrderId}
                  />
                );
              })}
            </ul>
          </SortableContext>
        )}
      </div>
    </section>
  );
}

function EmptyPool({
  query,
  allPoolCount,
}: {
  query: string;
  allPoolCount: number;
}): JSX.Element {
  if (query.trim().length > 0) {
    return (
      <p className="text-sm text-ink-2">
        No matches for "{query}".
      </p>
    );
  }
  if (allPoolCount === 0) {
    return (
      <p className="text-sm text-ink-2">
        No unassigned orders for this pharmacy.
      </p>
    );
  }
  return <p className="text-sm text-ink-2">All orders are assigned.</p>;
}

function PoolCard({
  order,
  onAdd,
  isDragging,
}: {
  order: PoolOrder;
  onAdd: (orderId: string) => void;
  isDragging: boolean;
}): JSX.Element {
  const sortable = useSortable({ id: order.id });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <li ref={sortable.setNodeRef} style={style}>
      <Card className="flex items-start gap-3">
        <button
          type="button"
          aria-label={`Drag order ${order.order_number}`}
          className="mt-1 cursor-grab touch-none rounded p-1 text-ink-2 hover:bg-bg-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <DragHandle />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">
            {order.order_number}
          </p>
          <p className="truncate text-sm text-ink-2">
            {order.patient_name ?? '—'}
          </p>
          <p className="truncate text-xs text-ink-2">{poolAddress(order)}</p>
        </div>
        <button
          type="button"
          onClick={() => onAdd(order.id)}
          aria-label={`Add order ${order.order_number} to route`}
          className="rounded-md border border-border bg-bg px-2 py-1 text-xs text-ink hover:bg-bg-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Add →
        </button>
      </Card>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Route column
// ---------------------------------------------------------------------------

function RouteColumn({
  pickup,
  routeOrderIds,
  ordersIndex,
  onRemove,
  activeOrderId,
}: {
  pickup: RouteStop | null;
  routeOrderIds: string[];
  ordersIndex: Map<string, PoolOrder>;
  onRemove: (orderId: string) => void;
  activeOrderId: string | null;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: 'route-droppable' });
  return (
    <section
      aria-label="Route stops in order"
      className="flex min-h-[calc(100vh-220px)] flex-col rounded-md border border-border bg-bg"
    >
      <header className="border-b border-border px-4 py-3">
        <h2 className="text-sm font-medium text-ink">
          Route stops{' '}
          <span className="text-ink-2 font-normal">
            ({routeOrderIds.length} dropoff{routeOrderIds.length === 1 ? '' : 's'})
          </span>
        </h2>
      </header>
      <div
        ref={setNodeRef}
        className={[
          'flex-1 overflow-y-auto p-3',
          isOver ? 'bg-bg-soft/60 outline outline-2 outline-primary/40' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <ol className="space-y-2">
          {pickup ? <PickupCard pickup={pickup} /> : null}
          {routeOrderIds.length === 0 ? (
            <li className="rounded-md border border-dashed border-border p-6 text-center text-sm text-ink-2">
              Drag orders here from the left, or click "Add →" on a card.
            </li>
          ) : (
            <SortableContext
              items={routeOrderIds}
              strategy={verticalListSortingStrategy}
            >
              {routeOrderIds.map((id, idx) => {
                const o = ordersIndex.get(id);
                if (!o) return null;
                return (
                  <DropoffCard
                    key={id}
                    order={o}
                    sequence={idx + 1}
                    onRemove={onRemove}
                    isDragging={id === activeOrderId}
                  />
                );
              })}
            </SortableContext>
          )}
        </ol>
      </div>
    </section>
  );
}

function PickupCard({ pickup }: { pickup: RouteStop }): JSX.Element {
  return (
    <li>
      <Card className="flex items-start gap-3 border-primary/30 bg-primary/5">
        <div
          aria-label="Pickup (locked at position 0)"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary text-sm font-bold text-white"
        >
          P
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-medium text-ink">
            Pharmacy pickup
            <LockIcon />
          </p>
          <p className="truncate text-xs text-ink-2">{dropoffAddress(pickup)}</p>
        </div>
      </Card>
    </li>
  );
}

function DropoffCard({
  order,
  sequence,
  onRemove,
  isDragging,
}: {
  order: PoolOrder;
  sequence: number;
  onRemove: (orderId: string) => void;
  isDragging: boolean;
}): JSX.Element {
  const sortable = useSortable({ id: order.id });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <li ref={sortable.setNodeRef} style={style}>
      <Card className="flex items-start gap-3">
        <button
          type="button"
          aria-label={`Reorder order ${order.order_number}`}
          className="mt-1 cursor-grab touch-none rounded p-1 text-ink-2 hover:bg-bg-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <DragHandle />
        </button>
        <div
          aria-label={`Position ${sequence}`}
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-bg-soft text-xs font-semibold text-ink"
        >
          {sequence}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">
            {order.patient_name ?? '—'}
          </p>
          <p className="truncate text-xs text-ink-2">{poolAddress(order)}</p>
          <p className="text-xs text-ink-2">{order.order_number}</p>
        </div>
        <button
          type="button"
          onClick={() => onRemove(order.id)}
          aria-label={`Remove order ${order.order_number} from route`}
          className="rounded-md border border-border bg-bg px-2 py-1 text-xs text-ink hover:bg-bg-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          ✕
        </button>
      </Card>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Locked (read-only) view for non-draft routes
// ---------------------------------------------------------------------------

function LockedView({
  pickup,
  dropoffs,
  poolCount,
}: {
  pickup: RouteStop | null;
  dropoffs: RouteStop[];
  poolCount: number;
}): JSX.Element {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section
        aria-label="Unassigned orders (locked)"
        className="rounded-md border border-border bg-bg-soft p-4"
      >
        <h2 className="text-sm font-medium text-ink">
          Unassigned orders <span className="text-ink-2">({poolCount})</span>
        </h2>
        <p className="mt-2 text-sm text-ink-2">
          The pool is not editable while this route is published.
        </p>
      </section>
      <section
        aria-label="Route stops (locked)"
        className="rounded-md border border-border bg-bg p-3"
      >
        <h2 className="px-2 text-sm font-medium text-ink">
          Route stops ({dropoffs.length} dropoff{dropoffs.length === 1 ? '' : 's'})
        </h2>
        <ol className="mt-2 space-y-2">
          {pickup ? <PickupCard pickup={pickup} /> : null}
          {dropoffs.map((s, idx) => (
            <li key={s.id}>
              <Card className="flex items-start gap-3">
                <div
                  aria-label={`Position ${idx + 1}`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-bg-soft text-xs font-semibold text-ink"
                >
                  {idx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">
                    {dropoffPatientLabel(s)}
                  </p>
                  <p className="truncate text-xs text-ink-2">
                    {dropoffAddress(s)}
                  </p>
                  <p className="text-xs text-ink-2">{dropoffOrderNumber(s)}</p>
                </div>
              </Card>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons (inline SVG, no icon-pack dependency)
// ---------------------------------------------------------------------------

function DragHandle(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <circle cx="5" cy="3" r="1.4" />
      <circle cx="11" cy="3" r="1.4" />
      <circle cx="5" cy="8" r="1.4" />
      <circle cx="11" cy="8" r="1.4" />
      <circle cx="5" cy="13" r="1.4" />
      <circle cx="11" cy="13" r="1.4" />
    </svg>
  );
}

function LockIcon(): JSX.Element {
  return (
    <svg
      aria-label="Locked"
      role="img"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="currentColor"
      className="text-ink-2"
    >
      <path d="M4 7V5a4 4 0 1 1 8 0v2h1v7H3V7h1zm2 0h4V5a2 2 0 0 0-4 0v2z" />
    </svg>
  );
}
